import React, { useEffect, useState, useRef } from 'react';
import PeriodBar, { usePeriod } from '../components/PeriodBar';
import { StatusBadge as RosterBadge } from '../components/StudentStatusBadges';

export default function LiveAttendance() {
  const [rawClasses, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  // 今の期間の授業だけ（出席を取るのは今の年度だけ）【2026-09-17】
  const period = usePeriod({ lockYear: true });
  const classes = period.filter(Array.from(new Map(rawClasses.map(c => [c.id, c])).values()));
  const [status,  setStatus]  = useState(null);
  const [live,    setLive]    = useState([]);
  const [err,     setErr]     = useState('');
  const [auto,    setAuto]    = useState(true);
  const intervalRef = useRef(null);

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  useEffect(() => {
    fetch('/api/schedule/classes', { headers: auth() })
      .then(r => r.json())
      .then(d => setClasses(d.classes || d.data || []));
  }, []);

  const fetchData = async (id) => {
    if (!id) return;
    try {
      const [sR, lR] = await Promise.all([
        fetch('/api/attendance/class/' + id, { headers: auth() }),
        fetch('/api/attendance/live/'  + id, { headers: auth() })
      ]);
      const s = await sR.json();
      const l = await lR.json();
      if (s.success) setStatus(s);
      if (l.success) setLive(l.attendances || []);
    } catch (e) { setErr(e.message); }
  };

  // 名簿外の打刻【2026-09-14】
  //   ビーコンは教員ごとなので、教室に居合わせただけの他クラスの学生でも打刻が通る。
  //   サーバーは記録だけして enrolled=false で返してくるので、ここで拾って見せる。
  const unenrolled = live.filter(r => r.enrolled === false);

  const addToRoster = async (row) => {
    if (!window.confirm(`${row.name}（${row.student_id}）を履修者に追加しますか？`)) return;
    setErr('');
    try {
      const res = await fetch('/api/schedule/classes/' + classId + '/enrollments', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId: row.student_id }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '追加に失敗しました');
      await fetchData(classId);
    } catch (e) { setErr(e.message); }
  };

  // 授業切り替え or 自動更新
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!classId) return;
    fetchData(classId);
    if (auto) {
      intervalRef.current = setInterval(() => fetchData(classId), 5000);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [classId, auto]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>リアルタイム出席状況</h1>
        <p>選択した授業の出席状況を5秒間隔で更新します。</p>
      </div>

      <div className="card">
        <div className="form-row">
          <label>授業</label>
          <PeriodBar period={period} />
          <select value={classId} onChange={e => setClassId(e.target.value)}>
            <option value="">— 選択してください —</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
          <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)}/>
          5秒ごとに自動更新
        </label>
      </div>

      {err && <div className="card" style={{borderLeft:'4px solid #ef4444'}}><p style={{color:'#c62828'}}>{err}</p></div>}

      {status && (
        <>
          <div className="stats-grid">
            <SmallStat label="履修者数" value={status.total} color="#3b82f6"/>
            <SmallStat label="出席"     value={status.present} color="#22c55e"/>
            <SmallStat label="遅刻"     value={status.late}    color="#f59e0b"/>
            <SmallStat label="欠席"     value={status.absent}  color="#ef4444"/>
          </div>

          <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between'}}>
              <h2>履修者の出席状況 ({status.date})</h2>
              <span style={{color:'#666'}}>出席率 <strong>{status.attendanceRate}%</strong></span>
            </div>
            <table className="data-table">
              <thead><tr><th>学籍番号</th><th>氏名</th><th>状態</th><th>打刻時刻</th></tr></thead>
              <tbody>
                {(status.students || []).map(s => (
                  <tr key={s.id}>
                    <td>{s.student_id}</td>
                    <td><strong>{s.name}</strong></td>
                    <td>
                      {s.has_attended
                        ? <StatusBadge status={s.status}/>
                        : <span style={{ color: '#888' }}>未打刻</span>}
                    </td>
                    <td>{formatTime(s.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {unenrolled.length > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className="card-header">
                <h2>履修者名簿に無い打刻（{unenrolled.length}件）</h2>
              </div>
              <p style={{ color: '#666', fontSize: '0.88rem', margin: '0 0 0.6rem' }}>
                打刻は記録されていますが、出席率の分母には入っていません。
                履修しているはずの学生なら「履修者に追加」を押してください。
                教室に居合わせただけの学生なら、そのままで構いません。
              </p>
              <table className="data-table">
                <thead><tr><th>時刻</th><th>学籍番号</th><th>氏名</th><th></th></tr></thead>
                <tbody>
                  {unenrolled.map((r, i) => (
                    <tr key={i}>
                      <td>{formatTime(r.timestamp)}</td>
                      <td>{r.student_id}</td>
                      <td>{r.name}</td>
                      <td>
                        <button className="btn-secondary" onClick={() => addToRoster(r)}>
                          履修者に追加
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card">
            <div className="card-header"><h2>ライブストリーム ({live.length}件)</h2></div>
            {live.length === 0 ? (
              <p style={{color:'#888', textAlign:'center'}}>本日の打刻はまだありません</p>
            ) : (
              <table className="data-table">
                <thead><tr><th>時刻</th><th>学籍番号</th><th>氏名</th><th>方法</th><th>状態</th></tr></thead>
                <tbody>
                  {live.map((r, i) => (
                    <tr key={i}>
                      <td>{formatTime(r.timestamp)}</td>
                      <td>{r.student_id}</td>
                      <td>
                        {r.name}
                        {r.enrolled === false && <RosterBadge kind="notOnRoster" />}
                      </td>
                      <td>{r.device_type || '-'}</td>
                      <td><StatusBadge status={r.status}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SmallStat({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    present: { text: '出席', bg: '#dcfce7', fg: '#166534' },
    late:    { text: '遅刻', bg: '#fef3c7', fg: '#92400e' },
    absent:  { text: '欠席', bg: '#fee2e2', fg: '#991b1b' },
    // 時間外・名簿外など、記録はしたが確認が要るもの【2026-09-14】
    irregular: { text: '要確認', bg: '#ffedd5', fg: '#9a3412' },
    early_leave: { text: '早退', bg: '#e0e7ff', fg: '#3730a3' },
  };
  const s = map[status] || { text: status, bg: '#e5e7eb', fg: '#374151' };
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px',
      background: s.bg, color: s.fg, fontSize: '0.8rem', fontWeight: 600
    }}>{s.text}</span>
  );
}

function formatTime(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second:'2-digit' });
  } catch { return '-'; }
}
